import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Div, Dropdown, Icon, Input, Row, Text } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import CONSTANTS from 'src/store/constants';
import { appActions, modelAddActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { DatasetsDropdown, Button } from 'src/components';

const defaultValues = {
  name: '',
  petsPercentage: null,
  predictionType: 'Litigation',
  typeDetail: '',
};

function AddClassifierModel({
  app: {
    profile,
    profile: { role, tenant },
  },
  modelAdd: { segments, fetchSegmentsStatus, addModelStatus, addModelError },
  fetchSegments,
  addModel,
  enqueueNotification,
  history,
}) {
  const [typeSelectOpen, setTypeSelectOpen] = useState(false);

  const [isAdmin, setIsAdmin] = useState(role === CONSTANTS.ROLE.SUPER_ADMIN || role === CONSTANTS.ROLE.ADMIN);
  const [positiveSegments, setPositiveSegments] = useState([]);
  const [negativeSegments, setNegativeSegments] = useState([]);
  const [validationSegments, setValidationSegments] = useState([]);
  const [positiveSegment, setPositiveSegment] = useState(null);
  const [negativeSegment, setNegativeSegment] = useState(null);
  const [validationSegment, setValidationSegment] = useState(null);

  const {
    handleSubmit,
    control,
    errors,
    register: registerInput,
    reset,
    setValue,
    getValues,
    trigger,
    watch,
  } = useForm({ defaultValues });
  const modelName = watch('name');
  const typeDetail = watch('typeDetail');
  const predictionType = watch('predictionType');
  const petsPercentage = Number(watch('petsPercentage'));

  const onSubmit = useCallback(
    (data) => {
      const { id: positiveSegmentId } = positiveSegment || {};
      const { id: negativeSegmentId } = negativeSegment || {};
      const { id: validationSegmentId } = validationSegment || {};
      addModel({
        ...data,
        name: modelName,
        dataModelType: 'CLASSIFIER', // NOTE: Make this selectable by type
        typeDetail,
        predictionType,
        petsPercentage,
        positiveSegmentId: positiveSegmentId || null,
        negativeSegmentId: negativeSegmentId || null,
        validationSegmentId: validationSegmentId || null,
        all_lift_info: {},
        lift_scores: {},
        payload: { profile },
      });
    },
    [
      addModel,
      modelName,
      negativeSegment,
      petsPercentage,
      positiveSegment,
      predictionType,
      profile,
      typeDetail,
      validationSegment,
    ]
  );

  const registerInputs = useCallback(() => {
    registerInput({ name: 'positiveSegmentId', type: 'custom' }, { required: 'Target Behavior is required' });
    registerInput(
      { name: 'negativeSegmentId', type: 'custom' },
      {
        validate: (value) =>
          (!watch('validationSegmentId') &&
            !value &&
            'Either Control Behavior Training Set or Blind Validation Set is needed') ||
          true,
      }
    );
    registerInput({ name: 'validationSegmentId', type: 'custom' }, { required: false });
  }, [watch, registerInput]);

  useEffect(() => {
    const query = {
      filters: [
        {
          and: [{ name: 'filter', op: 'eq', val: 'POPULATION_SNAPSHOT' }],
        },
      ],
    };
    fetchSegments(query);
    registerInputs();
  }, [tenant, registerInputs, fetchSegments]);

  useEffect(() => {
    const hit = segments.filter(({ tag }) => tag === CONSTANTS.SEGMENT.TAG.HIT);
    const miss = segments.filter(({ tag }) => tag === CONSTANTS.SEGMENT.TAG.MISS);
    const validation = segments.filter(({ tag }) => tag === CONSTANTS.SEGMENT.TAG.VALIDATION);
    setPositiveSegments(hit);
    setNegativeSegments(miss);
    setValidationSegments(validation);
  }, [segments]);

  useEffect(() => {
    setIsAdmin(role === CONSTANTS.ROLE.SUPER_ADMIN || role === CONSTANTS.ROLE.ADMIN);
  }, [role]);

  useEffect(() => {
    if (addModelStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Error when creating model. Reason: ${addModelError}`);
    }
    if (addModelStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Model is successfully created', { autoClose: true });
      setTimeout(() => {
        reset(defaultValues);
        setPositiveSegment(null);
        setNegativeSegment(null);
        setValidationSegment(null);
        history.push('/models');
      }, 2000);
    }
  }, [addModelStatus, addModelError, enqueueNotification, history, reset]);

  return (
    <>
      <Container>
        <Row justify="flex-end" p={{ t: '1em' }}>
          <Col>
            <Button
              buttonStyle="primary"
              onClick={() => {
                history.push('/models');
              }}
              data-tip="go back"
            >
              <Icon color="white" name="LongLeft" size="1rem" m={{ r: '0.75rem' }} />
              BACK
            </Button>
          </Col>
        </Row>
      </Container>
      <Container>
        <Div
          bg="white"
          rounded="md"
          textColor="black"
          m={{ t: '1.25em', b: '0', x: '0' }}
          p={{ x: '1.5rem', y: '1rem' }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Row p={{ t: '2.4em', b: '1em' }}>
              <Col size={{ xs: '12', md: '4' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="input">Prediction Type</Text>
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '8' }}>
                <Controller
                  name="predictionType"
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <Dropdown
                      h="1.875rem"
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      ref={ref}
                      isOpen={typeSelectOpen}
                      onClick={() => setTypeSelectOpen(!typeSelectOpen)}
                      onSelect={(idx) => onChange(CONSTANTS.TAL_MODEL.TYPES.slice(1)[idx])}
                      menu={CONSTANTS.TAL_MODEL.TYPES.slice(1)}
                      textColor="black"
                    />
                  )}
                  control={control}
                  rules={{
                    required: 'Model prediction type is required',
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="predictionType" />
                </Text>
              </Col>
            </Row>
            {{
              'Early Cancellation': (
                <>
                  <Row p={{ b: '1em' }}>
                    <Col size={{ xs: '12', md: '4' }}>
                      <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                        <Text textSize="input">Cancellation window in days</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '8' }}>
                      <Controller
                        h="1.875rem"
                        name="typeDetail"
                        as={Input}
                        control={control}
                        rules={{
                          required: 'Cancellation window is required',
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="typeDetail" />
                      </Text>
                    </Col>
                  </Row>
                </>
              ),
              'Premium Leakage': (
                <>
                  <Row p={{ b: '1em' }}>
                    <Col size={{ xs: '12', md: '4' }}>
                      <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                        <Text textSize="input">Subtype</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '8' }}>
                      <Controller
                        h="1.875rem"
                        name="typeDetail"
                        as={Input}
                        control={control}
                        rules={{
                          required: 'Subtype is required',
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="typeDetail" />
                      </Text>
                    </Col>
                  </Row>
                </>
              ),
              Other: (
                <>
                  <Row p={{ b: '1em' }}>
                    <Col size={{ xs: '12', md: '4' }}>
                      <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                        <Text textSize="input">Please describe</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '8' }}>
                      <Controller
                        h="1.875rem"
                        name="typeDetail"
                        as={Input}
                        control={control}
                        rules={{
                          required: 'Description is required',
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="typeDetail" />
                      </Text>
                    </Col>
                  </Row>
                </>
              ),
            }[getValues('predictionType')] || null}
            <Row p={{ b: '1em' }}>
              <Col size={{ xs: '12', md: '4' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="input">Name</Text>
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '8' }}>
                <Controller
                  h="1.875rem"
                  name="name"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'Model name is required',
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="name" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1em' }}>
              <Col size={{ xs: '12', md: '4' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="input">Risk frequency as a percentage</Text>
                  <Icon
                    name="Info"
                    m={{ x: '0.5rem' }}
                    size="12px"
                    data-tip="Please enter the proportion of risk-related records, as a percentage of the population where the prediction will be implemented. For example, if you are predicting early cancellation at point of quote, and 18.8% of customers cancel early, then please enter '18.8' in this field."
                  />
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '8' }}>
                <Controller
                  name="petsPercentage"
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <Input
                      h="1.875rem"
                      onChange={(e) => onChange(e.target.value)}
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      type="number"
                      ref={ref}
                      rounded={{ l: 'md' }}
                      suffix={
                        <Button rounded={{ r: 'md' }} bg="gray500" h="1.875rem">
                          %
                        </Button>
                      }
                    />
                  )}
                  control={control}
                  rules={{
                    required: 'HIT % is required',
                    min: { value: 0, message: 'The HIT % cannot be lesser than 0%' },
                    max: { value: 100, message: 'The HIT % cannot be greater than 100%' },
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="name" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1em' }}>
              <Col size={{ xs: '12', md: '4' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="input">{CONSTANTS.SEGMENT.TAG_NAME.HIT}</Text>
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '8' }}>
                <DatasetsDropdown
                  w="100%"
                  h="1.875rem"
                  loading={fetchSegmentsStatus === CONSTANTS.API.STARTED}
                  segments={positiveSegments}
                  segment={positiveSegment}
                  defaultText="Please select a dataset"
                  onSelectSegment={(segment) => {
                    setValue('positiveSegmentId', segment.id);
                    setPositiveSegment(segment);
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="positiveSegmentId" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1em' }}>
              <Col size={{ xs: '12', md: '4' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="input">{CONSTANTS.SEGMENT.TAG_NAME.MISS}</Text>
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '8' }}>
                <DatasetsDropdown
                  w="100%"
                  h="1.875rem"
                  loading={fetchSegmentsStatus === CONSTANTS.API.STARTED}
                  segments={negativeSegments}
                  segment={negativeSegment}
                  defaultText="Please select a dataset"
                  onSelectSegment={(segment) => {
                    setNegativeSegment(segment);
                    setValue('negativeSegmentId', segment.id);
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="negativeSegmentId" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1em' }}>
              <Col size={{ xs: '12', md: '4' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="input">{CONSTANTS.SEGMENT.TAG_NAME.VALIDATION}</Text>
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '8' }}>
                <DatasetsDropdown
                  w="100%"
                  h="1.875rem"
                  loading={fetchSegmentsStatus === CONSTANTS.API.STARTED}
                  segments={validationSegments}
                  segment={validationSegment}
                  defaultText="Please select a dataset"
                  onSelectSegment={(segment) => {
                    setValidationSegment(segment);
                    setValue('validationSegmentId', segment.id);
                    trigger('negativeSegmentId');
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="memberships" />
                </Text>
              </Col>
            </Row>
            <Row d="flex" justify="flex-end" p={{ b: '2.5em' }}>
              <Col>
                <Button buttonStyle="primary" disabled={!isAdmin} onClick={handleSubmit(onSubmit)}>
                  <Text color="gray400">SAVE</Text>
                  {addModelStatus === CONSTANTS.API.STARTED ? (
                    <Icon color="gray400" name="Loading" m={{ x: '0.5rem' }} />
                  ) : null}
                </Button>
              </Col>
            </Row>
          </form>
        </Div>
      </Container>
    </>
  );
}

const mapStateToProps = ({ app, modelAdd }) => ({
  app,
  modelAdd,
});

const mapDispatchToProps = (dispatch) => ({
  fetchSegments: (...params) => dispatch(modelAddActions.fetchSegments(...params)),
  addModel: (...params) => dispatch(modelAddActions.addModel(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

AddClassifierModel.propTypes = {
  app: PropTypes.shape({
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      tenant: PropTypes.string.isRequired,
    }),
  }).isRequired,
  modelAdd: PropTypes.shape({
    segments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fetchSegmentsStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
    addModelStatus: PropTypes.string.isRequired,
    addModelError: PropTypes.string.isRequired,
  }).isRequired,
  fetchSegments: PropTypes.func.isRequired,
  addModel: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(AddClassifierModel));
